import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { liveQuery } from 'dexie';
import { BehaviorSubject, startWith, Subscription, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Repository } from 'typeorm';
import {
  DatabaseSyncAction,
  EntityType,
  Action
} from '../models/databaseSync.model';
import { PaddockDexie } from '../models/paddock.model';
import { PropertyDexie } from '../models/property.model';
import { User } from '../models/user.model';
import { AuthService } from './auth.service';
import { ClippedLayerService } from './BaseLayerServices/clippedLayer.service';
import { DexieDatabaseService } from './dexieIndexDB.service';
import { OfflineService } from './offline.service';
import { PropertyService } from './property.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class PaddockService {
  private auth = inject(AuthService);
  private propertyService = inject(PropertyService);
  private clippedLayerService = inject(ClippedLayerService);
  private dexieService = inject(DexieDatabaseService);
  private http = inject(HttpClient);
  private connectionService = inject(OfflineService);
  private toastService = inject(ToastService);

  paddocks = this.propertyService.activeProperty.pipe(
    switchMap((value) => {
      return value == null
        ? []
        : liveQuery(() =>
            this.dexieService.paddocks
              .where({ propertyID: value.propertyID })
              .toArray()
          );
    }),
    startWith([])
  );

  private userSub: Subscription;

  private databaseSub: Subscription;

  user: User;
  activeProperty: PropertyDexie;
  isOnline = true;
  isGuest = false;

  constructor() {
    const auth = this.auth;
    const connectionService = this.connectionService;

    connectionService.isOnline.subscribe((status) => (this.isOnline = status));
    this.userSub = auth.user.subscribe((user) => {
      this.user = user;
      this.isGuest = user.isGuest;
    });
  }

  async save(paddock: PaddockDexie) {
    await this.dexieService.paddocks.add(paddock);

    if (!this.isGuest) {
      if (this.isOnline) {
        this.http
          .post(environment.apiURL + 'Paddock/add', paddock)
          .pipe()
          .subscribe((resp) =>
            this.toastService.showSuccess('Paddock created and synced')
          );
      } else {
        this.dexieService.databaseSyncActions.add(
          new DatabaseSyncAction(
            EntityType.Paddock,
            paddock.paddockID,
            Action.Insert
          )
        );
        this.toastService.showSuccess('Paddock created locally');
      }
    }
  }

  async remove(paddock: PaddockDexie) {
    await this.dexieService.paddocks.delete(paddock.paddockID);

    await this.dexieService.paddockClippedLayers
      .where({ paddockID: paddock.paddockID })
      .delete();
    await this.dexieService.enterpriseReports
      .where({ paddockID: paddock.paddockID })
      .delete();
    await this.dexieService.topexReports
      .where({ paddockID: paddock.paddockID })
      .delete();
    await this.dexieService.npvReports
      .where({ paddockID: paddock.paddockID })
      .delete();

    if (!this.isGuest) {
      if (this.isOnline) {
        this.http
          .post(
            environment.apiURL +
              `Paddock/delete?paddockID=${paddock.paddockID}`,
            ''
          )
          .pipe()
          .subscribe((resp) =>
            this.toastService.showSuccess('Paddock deleted and synced')
          );
      } else {
        this.dexieService.databaseSyncActions.add(
          new DatabaseSyncAction(
            EntityType.Paddock,
            paddock.paddockID,
            Action.Remove
          )
        );
        this.toastService.showSuccess('Paddock deleted locally');
      }
    }
  }

  async removeAll(property) {
    let loadedPaddocks = await this.dexieService.paddocks
      .where({ propertyID: property.propertyID })
      .toArray();
    await this.dexieService.paddocks.bulkDelete(
      loadedPaddocks.map((paddock) => paddock.paddockID)
    );
  }

  async update(paddock: PaddockDexie) {
    await this.dexieService.paddocks.update(paddock.paddockID, paddock);

    if (!this.isGuest) {
      if (this.isOnline) {
        this.http
          .post(environment.apiURL + 'Paddock/update', paddock)
          .pipe()
          .subscribe((resp) =>
            this.toastService.showSuccess('Paddock updated and synced')
          );
      } else {
        this.dexieService.databaseSyncActions.add(
          new DatabaseSyncAction(
            EntityType.Paddock,
            paddock.paddockID,
            Action.Update
          )
        );
        this.toastService.showSuccess('Paddock updated locally');
      }
    }
  }
}
